/**
 * Event Tracker 埋点 SDK 配置与使用
 *
 * @description 轻量级埋点 SDK，支持事件上报、自动路由追踪、Session 管理等功能
 *
 * ===== 使用方式 =====
 *
 * 1. 【基础用法】手动上报事件
 * ```ts
 * import { track } from '@/plugins/event-tracker'
 *
 * // 普通埋点（常规上报，走队列 + XHR）
 * track('button_click', { title: '确认' })
 *
 * // 关键场景埋点（返回、关闭等，App 环境自动使用客户端代理）
 * track('page_close', { title: '关闭' }, { immediate: true })
 * ```
 *
 * 2. 【页面关闭场景】使用 sendBeacon 上报
 * ```ts
 * import { trackBeacon } from '@/plugins/event-tracker'
 *
 * // 使用 sendBeacon 立即上报（用于页面关闭等场景）
 * trackBeacon('chat_close_click', { title: '对话页_关闭' })
 * router.back()  // 之后立即跳转也不会丢失埋点
 * ```
 *
 * 3. 【进阶用法】使用 SDK 实例
 * ```ts
 * import { tracker } from '@/plugins/event-tracker'
 *
 * // 上报事件
 * tracker.event('page_view', { title: '首页' })
 *
 * // 使用 sendBeacon 上报
 * tracker.beaconEvent('close_click', { title: '关闭' })
 *
 *
 * // 启用/禁用
 * tracker.enable()
 * tracker.disable()
 * ```
 *
 * 4. 【组合式 API】在 Vue 组件中使用
 * ```vue
 * <script setup>
 * import { track, trackBeacon } from '@/plugins/event-tracker'
 *
 * const handleClick = () => {
 *   track('button_click', { title: '确认'})
 * }
 *
 * const handleClose = () => {
 *   trackBeacon('close_click', { title: '关闭' })
 *   router.back()
 * }
 * </script>
 * ```
 *
 * 5. 【指令方式】使用 v-track 指令
 * ```vue
 * <template>
 *   <button v-track="{ event: 'button_click', params: { title: '提交'} }">
 *     提交
 *   </button>
 * </template>
 * ```
 *
 * 6. 【动态配置用户信息】
 * ```ts
 * import { setUser } from '@/plugins/event-tracker'
 *
 * // 用户登录后配置用户信息
 * setUser({
 *   user_unique_id: '12345', // 用户唯一标识，可以是你的系统登录用户id
 * })
 * ```
 *
 * ===== API 说明 =====
 * - track(event, params, options?)              常规上报（走队列 + XHR）
 *   - options.immediate: true                   关键场景（返回/关闭等）
 *     * App 环境：自动使用客户端代理
 *     * H5 环境：自动使用 sendBeacon
 * - trackBeacon(event, params)                  使用 sendBeacon 立即上报（手动调用）
 * - trackWithNative(event, params)              强制使用客户端代理上报（通常不需要直接调用）
 *
 * ===== 自动功能 =====
 * - ✅ 自动路由追踪：页面曝光、停留时长
 * - ✅ 自动用户信息：从 userStore 自动获取
 * - ✅ 自动批量上报：达到数量或定时触发
 * - ✅ 自动客户端信息：设备、浏览器、屏幕等
 * - ✅ 页面卸载时自动使用 sendBeacon 上报队列中的事件
 *
 * ===== 配置说明 =====
 * 在 main.ts 中已自动初始化，无需额外配置
 * 如需修改配置，请编辑本文件的 setupEventTracker 函数
 */
import sdk from '@/lib/event-tracker'
import { reportBatchEventsBeacon, reportEvents } from '@/api/eventlog'
import { useUserStore } from '@/stores/user'
import router from '@/router'

const isApp = () => /hncyQxApp/i.test(navigator.userAgent)

// 临时标志：下一次上报是否使用客户端代理
let useNativeForNextReport = false

/**
 * 初始化埋点 SDK
 */
export function setupEventTracker() {
  // 初始化 SDK
  sdk.init({
    // 是否启用
    enable: true,

    // 是否开启调试模式（会在控制台打印日志）
    debug: import.meta.env.VITE_APP_ENV === 'dev',

    // 批量上报数量（达到此数量自动上报）
    batchSize: 10,

    // 常规上报函数（必须）
    reportLogs: async (data) => {
      try {
        // 根据临时标志决定是否使用客户端代理
        const useNative = useNativeForNextReport
        useNativeForNextReport = false // 重置标志

        await reportEvents(data, { useNativeTracker: useNative })
      } catch (error) {
        console.error('[Event Tracker] 上报失败:', error)
        // 不要 throw error，防止 SDK 一直重试失败的埋点
        // 埋点上报失败应该静默处理，不影响业务逻辑
      }
    },

    // sendBeacon 上报函数（用于页面卸载等关键时刻）
    // 如不配置：beaconEvent() 无法使用，页面卸载时会降级到常规上报
    reportLogsBeacon: (data) => {
      console.log('[Event Tracker] 使用 sendBeacon 上报数据', data)
      return reportBatchEventsBeacon(data)
    },

    // 获取用户信息（可选）
    getUserInfo: () => {
      const userStore = useUserStore()
      if (userStore.userInfo?.uuid) {
        return {
          user_unique_id: userStore.userInfo.uuid,
          uuid: userStore.userInfo.uuid,
          nickName: userStore.userInfo.nickName,
          sex: userStore.userInfo.sex,
          gender: userStore.userInfo.gender,
          status: userStore.userInfo.status,
          token: userStore.userInfo.token,
        }
      }
      return undefined
    },

    // 全局公共参数（可选）
    globalParams: {
      app_name: 'qx-h5',
      app_version: '1.0.0',
      env: import.meta.env.VITE_APP_ENV,
    },

    // 动态属性获取器（每次上报时实时获取）
    dynamicPropsGetter: () => {
      return {
        page_url: window.location.href,
        page_title: document.title,
      }
    },

    // App ID 和域名（用于客户端信息采集）
    app_id: 10001,
    domain: window.location.hostname,
    cookie_expire: 365 * 86400000, // 365 天

    // Session 配置
    session: {
      storageKey: 'qx_event_tracker_session',
      maxSessionDuration: 24 * 60 * 60 * 1000, // 24小时
      maxIdleDuration: 30 * 60 * 1000, // 30分钟无操作则创建新 session
    },

    // 离线缓存配置
    cache: {
      enable: false,
      key: 'qx_event_tracker_cache',
      maxSize: 50, // 最多缓存 100 条
    },

    // 路由埋点配置（使用原生监控方式）
    routerTracking: {
      enable: !isApp(), // App 环境禁用（使用 app-page-tracking.ts），H5 环境启用
      autoPageView: true, // 自动上报页面曝光
      autoPageStay: true, // 自动上报页面停留时长
      spa: true, // SPA 模式（劫持 pushState/replaceState）
      autoTrack: true, // 自动追踪原生路由变化（popstate/hashchange）
      allowHash: false, // 追踪 hash 路由
      pageViewEvent: 'page_view', // 页面曝光事件名
      pageStayEvent: 'page_stay', // 页面停留事件名
      onReady: () => router.isReady(), // 等待初始导航（含异步 guard）完成后再上报首次 page_view
    },

    // 上报前钩子（可用于过滤或修改事件）
    onBeforeTrack: (event) => {
      if (event.event === 'page_view' || event.event === 'page_stay') {
        try {
          const params = event.params || {}
          const eventUrl: string = (params.url as string) || window.location.href

          // 提取 hash
          const hashMatch = eventUrl.match(/#([^?]+)/)

          let trackName = ''

          if (hashMatch && hashMatch[1]) {
            // 有 hash，直接使用
            trackName = hashMatch[1]
          } else {
            // 无 hash，根据事件类型选择策略
            if (event.event === 'page_view') {
              // ✅ page_view: 刚进入页面，currentRoute 是正确的且更快
              const currentRoute = router.currentRoute.value
              trackName = (currentRoute.meta?.track_name as string) || currentRoute.path
            } else {
              // ✅ page_stay: 已离开页面，必须从 params.url 解析
              try {
                const url = new URL(eventUrl, window.location.origin)
                const matched = router.resolve(url.pathname)
                trackName = (matched.meta?.track_name as string) || matched.path
              } catch {
                // 降级
                const url = new URL(eventUrl, window.location.origin)
                trackName = url.pathname
              }
            }
          }

          if (trackName) {
            trackName = trackName
              .replace(/^\/+|\/+$/g, '')
              .replace(/\//g, '_')
              .replace(/:/g, '')
              .toLowerCase()
              // 如果trackName 没有值，那么不上报页面停留，避免不存在页面上报问题
            if (!trackName) {
              return false
            }
            event.event = `${trackName}_${event.event}`
          }
        } catch (error) {
          console.warn('[Event Tracker] 获取路由信息失败', error)
        }
      }
      return event
    },

    // 错误处理钩子
    onError: (error, context) => {
      console.error('[Event Tracker] 错误', error, context)
    },
  })

  // 启动 SDK
  sdk.start()

  console.log('[Event Tracker] 初始化完成')
}

/**
 * 手动上报事件（常规上报，走队列 + XHR）
 *
 * @param event 事件名
 * @param params 事件参数
 * @param options 配置项
 * @param options.immediate 是否立即上报（用于关键场景，如返回、关闭等）
 *
 * @example
 * ```ts
 * // 普通埋点
 * track('button_click', { title: '提交' })
 *
 * // 关键场景埋点（返回、关闭等）
 * // App 环境：使用客户端代理
 * // H5 环境：使用 sendBeacon
 * track('page_close', { title: '关闭' }, { immediate: true })
 * ```
 */
export function track(
  event: string,
  params?: Record<string, unknown>,
  options?: { immediate?: boolean }
) {
  const { immediate = false } = options || {}

  if (immediate) {
    // 关键场景：根据环境选择最佳上报方式
    if (isApp()) {
      // App 环境：使用客户端代理（防止 webview 销毁导致埋点丢失）
      trackWithNative(event, params)
    } else {
      // H5 环境：使用 sendBeacon（防止页面卸载导致埋点丢失）
      trackBeacon(event, params)
    }
  } else {
    // 普通场景：正常入队
    sdk.event(event, params)
  }
}

/**
 * 使用 sendBeacon 立即上报（用于页面关闭等场景）
 * 不走队列，直接上报，适合页面卸载时使用
 */
export function trackBeacon(event: string, params?: Record<string, unknown>) {
  sdk.beaconEvent(event, params)
}

/**
 * 使用客户端代理上报（App 环境下，防止 webview 销毁导致埋点丢失）
 *
 * @description
 * 将事件直接通过客户端代理上报，不走 HTTP 请求。
 * 适用场景：
 * - App 切换到后台（tcAppOnPause）
 * - 页面即将关闭时
 * - 需要确保埋点不丢失的关键场景
 *
 * @param event 事件名
 * @param params 事件参数
 * @returns Promise<void>
 *
 * @example
 * ```ts
 * // App 切后台时上报页面停留
 * tcAppOnPause(() => {
 *   await trackWithNative('page_stay', { duration: 5000 })
 * })
 * ```
 */
export async function trackWithNative(event: string, params?: Record<string, unknown>): Promise<void> {
  // H5 环境降级为普通 track
  if (!isApp()) {
    console.warn('[Event Tracker] trackWithNative 在 H5 环境下降级为 track')
    track(event, params)
    return
  }

  // 设置标志，下一次 reportLogs 使用客户端代理
  useNativeForNextReport = true

  // 先将事件加入队列
  sdk.event(event, params)

  // 强制上报队列，使用客户端代理
  sdk.flush(false) // flush 队列

  // 等待一下让 reportLogs 被调用
  await new Promise(resolve => setTimeout(resolve, 50))
}

/**
 * 动态配置用户信息
 */
export function setUser(userInfo: { user_unique_id?: string; [key: string]: unknown }) {
  sdk.setConfig({
    getUserInfo: () => userInfo,
  })
}

/**
 * 导出 SDK 实例
 */
export { sdk as tracker }

declare module 'vue' {
  interface ComponentCustomProperties {
    $tracker: typeof sdk
  }
}
